/**
 * V2 Invoice Deduction Constants
 * Centralized configuration for invoice deductions, article numbers, and units
 * This file serves as the single source of truth for all deduction-related constants in V2
 */

// ==================== TYPES ====================

export type DeductionType =
  | 'mileage_report'
  | 'allowance_in_sweden'
  | 'allowance_outside_sweden'
  | 'travel_expenses_pdf'
  | 'expenses_materials_tools_pdf';

export interface TaxFreeArticleConfig {
  articleNumber: string;
  unit: string;
  hasVAT: boolean;
  ratePerUnit: number | null;
  halfDayRate?: number;
}

// ==================== ARTICLE NUMBER MAPPINGS ====================

/**
 * Article Number to Deduction Type Mapping
 * Used when converting copied invoice data to deduction types
 */
export const ARTICLE_NUMBER_TO_DEDUCTION_TYPE: Record<string, DeductionType> = {
  // Mileage Report
  '87': 'mileage_report',              // Mileage Report (Tax Free)
  '88': 'mileage_report',              // Mileage Report (Non Tax Free)
  
  // Travel Expenses
  '91': 'travel_expenses_pdf',         // Travel Expenses (Tax Free)
  
  // Materials & Tools
  '1': 'expenses_materials_tools_pdf', // Materials & Tools (Non Tax Free)
  '2': 'expenses_materials_tools_pdf', // Materials & Tools (Tax Free)
  
  // Allowance Outside Sweden
  '85': 'allowance_outside_sweden',    // Allowance Outside Sweden (Tax Free)
  '86': 'allowance_outside_sweden',    // Allowance Outside Sweden (Non Tax Free)
  
  // Allowance In Sweden
  '81': 'allowance_in_sweden',         // Allowance In Sweden (Tax Free)
  '82': 'allowance_in_sweden',         // Allowance In Sweden (Non Tax Free)
};

/**
 * Tax-Free Article Configuration Map
 * Complete configuration for each deduction type including article numbers, units, and rates
 */
export const TAX_FREE_ARTICLE_CONFIG: Record<DeductionType, TaxFreeArticleConfig> = {
  mileage_report: {
    articleNumber: '93',      // Mileage Report Article Number (v2)
    unit: 'Km',               // Kilometer
    hasVAT: false,
    ratePerUnit: 2.5,         // 2.5 SEK per km
  },
  allowance_in_sweden: {
    articleNumber: '101',     // Allowance In Sweden Article Number (v2)
    unit: 'Day',              // Day
    hasVAT: false,
    ratePerUnit: 300.0,       // 300 SEK per day (full day)
    halfDayRate: 150.0,       // 150 SEK per day (half day)
  },
  allowance_outside_sweden: {
    articleNumber: '100',     // Allowance Outside Sweden Article Number (v2)
    unit: 'Day',              // Day
    hasVAT: false,
    ratePerUnit: null,        // Country specific rate
  },
  travel_expenses_pdf: {
    articleNumber: '98',      // Travel Expenses Article Number (v2)
    unit: 'pcs',              // pieces
    hasVAT: true,
    ratePerUnit: null,        // User enters amount
  },
  expenses_materials_tools_pdf: {
    articleNumber: '99',      // Material Expenses Article Number (v2)
    unit: 'pcs',              // pieces
    hasVAT: true,
    ratePerUnit: null,        // User enters amount
  },
};

// ==================== HELPER FUNCTIONS ====================

/**
 * Get deduction type from article number
 * @param articleNumber - The article number from the invoice
 * @returns Deduction type or default 'mileage_report'
 */
export const getDeductionTypeFromArticleNumber = (articleNumber: string): DeductionType => {
  return ARTICLE_NUMBER_TO_DEDUCTION_TYPE[articleNumber] || 'mileage_report';
};

/**
 * Get complete article configuration for a deduction type
 * @param deductionType - The type of deduction
 * @returns Article configuration or null if not found
 */
export const getTaxFreeArticleConfig = (deductionType: string): TaxFreeArticleConfig | null => {
  return TAX_FREE_ARTICLE_CONFIG[deductionType as DeductionType] || null;
};

/**
 * Get article number for a deduction type
 * @param deductionType - The type of deduction
 * @returns Article number or empty string if not found
 */
export const getTaxFreeArticleNumber = (deductionType: string): string => {
  return TAX_FREE_ARTICLE_CONFIG[deductionType as DeductionType]?.articleNumber || '';
};

/**
 * Get unit for a deduction type
 * @param deductionType - The type of deduction
 * @returns Unit or 'Pc' as default if not found
 */
export const getTaxFreeArticleUnit = (deductionType: string): string => {
  return TAX_FREE_ARTICLE_CONFIG[deductionType as DeductionType]?.unit || 'Pc';
};

/**
 * Get rate per unit for a deduction type
 * @param deductionType - The type of deduction
 * @returns Rate per unit or null if not found
 */
export const getTaxFreeArticleRate = (deductionType: string): number | null => {
  return TAX_FREE_ARTICLE_CONFIG[deductionType as DeductionType]?.ratePerUnit || null;
};

/**
 * Check if a deduction type has VAT
 * @param deductionType - The type of deduction
 * @returns True if has VAT, false otherwise
 */
export const hasVAT = (deductionType: string): boolean => {
  return TAX_FREE_ARTICLE_CONFIG[deductionType as DeductionType]?.hasVAT || false;
};

/**
 * Check if an article number is a valid deduction article
 * @param articleNumber - The article number to check
 * @returns True if it's a valid deduction article number
 */
export const isValidDeductionArticleNumber = (articleNumber: string): boolean => {
  return articleNumber in ARTICLE_NUMBER_TO_DEDUCTION_TYPE;
};

/**
 * Get all valid deduction article numbers
 * @returns Array of valid article numbers
 */
export const getValidDeductionArticleNumbers = (): string[] => {
  return Object.keys(ARTICLE_NUMBER_TO_DEDUCTION_TYPE);
};

/**
 * Get all deduction types
 * @returns Array of all deduction types
 */
export const getAllDeductionTypes = (): DeductionType[] => {
  return Object.keys(TAX_FREE_ARTICLE_CONFIG) as DeductionType[];
};

// ==================== CONSTANTS ====================

/**
 * Default deduction type when none is specified
 */
export const DEFAULT_DEDUCTION_TYPE: DeductionType = 'mileage_report';

/**
 * Default unit when none is found
 */
export const DEFAULT_UNIT = 'Pc';

/**
 * Deduction types that include VAT
 */
export const DEDUCTION_TYPES_WITH_VAT: DeductionType[] = [
  'travel_expenses_pdf',
  'expenses_materials_tools_pdf',
];

/**
 * Deduction types that are tax-free
 */
export const TAX_FREE_DEDUCTION_TYPES: DeductionType[] = [
  'mileage_report',
  'allowance_in_sweden',
  'allowance_outside_sweden',
];
